import { createFileRoute } from "@tanstack/react-router";
import { DocPage } from "@/components/DocPage";

export const Route = createFileRoute("/rating-policy")({
  head: () => ({
    meta: [
      { title: "Rating and Strike Policy — Want To Buy" },
      { name: "description", content: "How ratings, strikes, suspensions, Trusted Seller, and Trusted Buyer status work on the Want To Buy connection platform." },
    ],
  }),
  component: RatingPolicyPage,
});

export function RatingPolicyContent() {
  return (
    <>
      <p className="!mt-0 text-xs uppercase tracking-wider text-muted-foreground">Last updated: July 2026</p>

      <section>
        <h2>What strikes cover</h2>
        <p>
          Because Want To Buy does not mediate individual transaction disputes, strikes cover only <strong>platform-integrity violations</strong>. Sellers accumulate strikes for any of the following:
        </p>
        <ul>
          <li>Failure to submit the mandatory 2 timestamped photos within <strong>48 hours</strong> of offer acceptance.</li>
          <li>Failure to respond to an accepted connection within <strong>48 hours</strong>.</li>
          <li>Confirmed ban evasion (a new account operated by a previously banned user).</li>
          
          <li>Confirmed counterfeit card sale reported and verified through the report system.</li>
          <li>Harassment confirmed through admin message review.</li>
        </ul>
        <p>
          Transaction outcome disputes (condition disagreements, non-delivery, payment issues) do <strong>not</strong> result in Want To Buy strikes — those must be resolved between the parties, typically through PayPal's Resolution Centre when PayPal Goods &amp; Services was used.
        </p>
      </section>

      <section>
        <h2>Suspensions and bans</h2>
        <ul>
          <li>At <strong>3 strikes</strong>, the account is automatically suspended pending admin review.</li>
          <li>Admins may reinstate accounts with conditions (e.g. reduced posting limits) after review.</li>
          <li>At <strong>5 total lifetime strikes after at least one reinstatement</strong>, the account is permanently banned.</li>
          <li>Severe violations — confirmed counterfeits, confirmed ban evasion, confirmed fraud — skip the strike system and trigger immediate permanent bans.</li>
        </ul>
      </section>

      <section>
        <h2>Buyer accountability strikes</h2>
        <p>
          After a buyer approves the seller's mandatory timestamped photos, they should follow through with the transaction as agreed. Buyers who repeatedly approve photos and then ghost the seller receive one strike per confirmed abandonment. At 3 buyer strikes, the buyer's ability to have offers accepted is suspended pending admin review.
        </p>
      </section>

      <section>
        <h2>How seller rating is calculated</h2>
        <p>
          A seller's rating is the <strong>percentage of completed transactions with no active strikes</strong>. Rating is recomputed in real time from ratings submitted by counterparties via the mutual reference system.
        </p>
      </section>

      <section>
        <h2>Trusted Seller status</h2>
        <ul>
          <li>Granted automatically at <strong>100+ completed transactions</strong> and a <strong>99%+ clean rating</strong>.</li>
          <li>Trusted Sellers get a visible badge on their profile and on every offer they submit, plus priority placement on tied-price offers.</li>
          <li>Status is suspended automatically if rating drops below 97% and reinstated when it recovers to 99%+.</li>
        </ul>
      </section>

      <section>
        <h2>Trusted Buyer status</h2>
        <ul>
          <li>Granted automatically to accounts with at least 1 year of activity, a 99%+ clean rating, and consistent follow-through after approving mandatory photos.</li>
          <li>Trusted Buyers receive a visible badge on their public profile and buy orders.</li>
        </ul>
      </section>

      <section>
        <h2>Ratings, reports, and mutual references — kept</h2>
        <p>
          The mutual reference system, the user report system, and the banned accounts list remain in place. These are platform-integrity mechanisms — they are not a substitute for dispute resolution between users, which happens through the payment provider.
        </p>
      </section>
    </>
  );
}

function RatingPolicyPage() {
  return (
    <DocPage
      eyebrow="Platform Rules"
      title="Rating and Strike Policy"
      intro="A transparent, behaviour-based system that rewards reliable users and removes bad actors from the connection platform."
    >
      <RatingPolicyContent />
    </DocPage>
  );
}
